import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { CheckOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { Col, Divider, Row } from 'antd';

import MyModal from 'components/MyModal';
import Spin from 'components/Spin';
import {
  useProductCampaignTracker,
  useVerifyProductDomain,
  useCreateOrUpdateCampaignTracker,
  useDeleteCampaignTracker
} from 'v2/hooks/products/useProductCampaignTracker';

import Advanced from './components/Advanced';
import Basic from './components/Basic';
import Platforms from './components/Platforms';
import type { enTypePlatform } from './components/Platforms';
import TagmanagerAlert from './components/TagmanagerAlert';

interface IProps {
  open: boolean;
  handleClose: () => void;
  product: {
    id: number;
  };
  campaign: {
    id: number;
  };
}

interface ITrackerState {
  id: string;
  step: number;
  advanced: boolean;
  platformId: number;
  platformType: enTypePlatform;
  hasCampaign: boolean;
  hasPrice: boolean;
  hasBankslipPurchase: boolean;
  bankslipPurchase: string;
  domain?: string;
  hasDomain: boolean;
  tokenApi: string;
  pixel: string;
  registered: string[];
  isFetching: boolean;
  tracker: any;
  pages: string[];
  paymentMethods: {
    credit_card: number;
    bankslip: number;
    paypal: number;
    pix: number;
  };
  purchaseValue: string;
  sendCampaign: boolean;
  conversionLabel: string;
  validated: boolean;
}

const INITIAL_STATE: ITrackerState = {
  id: '',
  step: 1,
  platformId: null,
  platformType: null,
  pages: ['checkout', 'thank_you_card', 'thank_you_bankslip', 'bankslip_view'],
  sendCampaign: true,
  advanced: false,
  domain: null,
  validated: false,
  pixel: '',
  registered: [],
  hasCampaign: true,
  hasPrice: true,
  hasBankslipPurchase: false,
  hasDomain: false,
  tokenApi: null,
  bankslipPurchase: 'send_purchase',
  isFetching: false,
  tracker: null,
  purchaseValue: 'send_purchase_value',
  conversionLabel: '',
  paymentMethods: {
    credit_card: 0,
    bankslip: 0,
    paypal: 0,
    pix: 0
  }
};

const TrackerForm2: React.FC<IProps> = ({ open, handleClose, product }) => {
  const [state, setState] = useState<ITrackerState>(INITIAL_STATE);

  const { trackerData, isFetching: isLoadingTracker, refetch: refetchTracker } = useProductCampaignTracker(product.id);
  const verifyDomainMutation = useVerifyProductDomain();
  const createTrackerMutation = useCreateOrUpdateCampaignTracker();
  const deleteTrackerMutation = useDeleteCampaignTracker();

  useEffect(() => {
    if (trackerData) {
      const registered = trackerData.pixel ? trackerData.pixel.map((item: any): string[] => item.type) : [];
      const hasDomain = trackerData.pixel
        ? trackerData.pixel.find((item: any): boolean => item.type === 'facebook')
        : false;
      setState(prev => ({
        ...prev,
        isFetching: false,
        tracker: trackerData,
        registered,
        hasDomain: !!hasDomain
      }));
    }
  }, [trackerData]);

  const handleVerifyDomain = useCallback(async () => {
    try {
      if (state.domain && state.pixel?.trim().length) {
        setState(prev => ({ ...prev, isFetching: true }));
        const result = await verifyDomainMutation.mutateAsync(product.id);
        const { domain: verifiedDomain, validated } = result?.data ?? {};
        setState(prev => ({
          ...prev,
          domain: verifiedDomain,
          validated,
          isFetching: false,
          hasDomain: !!verifiedDomain
        }));
      }
    } catch (err) {
      toast.info('Pixel sem domínio cadastrado.');
      setState(prev => ({ ...prev, isFetching: false }));
    }
  }, [state.domain, state.pixel, product.id, verifyDomainMutation]);

  const handlePageChange = useCallback(
    (name: string) => () => {
      setState(prev => {
        const pages = [...prev.pages];
        const index = pages.findIndex(k => k === name);

        if (index === -1) {
          pages.push(name);
        } else if (pages.length > 1) {
          pages.splice(index, 1);
        }

        return { ...prev, pages };
      });
    },
    []
  );

  const handleRadioChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, purchaseValue: event.target.value }));
  }, []);

  const handleAdvanced = useCallback(() => {
    setState(prev => ({ ...prev, advanced: !prev.advanced }));
  }, []);

  const handlePixel = useCallback((pixel: string) => {
    setState(prev => ({ ...prev, pixel }));
  }, []);

  const handleDomain = useCallback((domain: string) => {
    setState(prev => ({ ...prev, domain: domain.replace(/ /g, '') }));
  }, []);

  const handleTokenApi = useCallback((token: string) => {
    setState(prev => ({ ...prev, tokenApi: token.replace(/ /g, '') }));
  }, []);

  const handleConversionLabel = useCallback((conversionLabel: string) => {
    setState(prev => ({ ...prev, conversionLabel }));
  }, []);

  const handleBack = useCallback(() => {
    setState(prev => ({ ...prev, step: 1, domain: '' }));
  }, []);

  const handlePlatform = useCallback(
    (
      platformId: number,
      platformType: enTypePlatform,
      hasCampaign: boolean,
      hasPrice: boolean,
      hasBankslipPurchase: boolean,
      step: number
    ) => {
      setState(prev => {
        const platform = prev.tracker?.pixel?.find((x: any) => x.type === platformType);

        if (platform) {
          let purchaseValue = 'never';

          if (platform.hasPurchaseValue) {
            purchaseValue = 'send_purchase_value';
          }

          if (platform.hasCustomValues) {
            purchaseValue = 'custom_values';
          }

          return {
            ...prev,
            step,
            platformType,
            platformId,
            hasCampaign,
            hasBankslipPurchase,
            hasPrice,
            id: prev.tracker.id,
            pixel: platform.pixelId,
            pages: platform.enabledPages,
            paymentMethods: platform.forcePaymentValues,
            purchaseValue,
            bankslipPurchase: 'send_purchase',
            conversionLabel: platform.conversionLabel,
            tokenApi: platform.tokenApi,
            domain: platform.domain,
            validated: platform.validated
          };
        }

        return {
          ...prev,
          step,
          platformType,
          platformId,
          hasCampaign,
          hasBankslipPurchase,
          hasPrice,
          id: '',
          pixel: '',
          conversionLabel: '',
          pages: ['checkout', 'thank_you_card', 'thank_you_bankslip', 'bankslip_view'],
          paymentMethods: { credit_card: 0, bankslip: 0, paypal: 0, pix: 0 },
          purchaseValue: 'send_purchase_value',
          bankslipPurchase: 'send_purchase'
        };
      });
    },
    []
  );

  const checkCustomValue = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => {
      const paymentMethods = { ...prev.paymentMethods };
      const key = event.target.value as keyof ITrackerState['paymentMethods'];
      paymentMethods[key] = paymentMethods[key] > 0 ? 0 : 1;
      return { ...prev, paymentMethods };
    });
  }, []);

  const handleCustomValue = useCallback((value: any, number: any) => {
    setState(prev => {
      const paymentMethods = { ...prev.paymentMethods };
      const key = value as keyof ITrackerState['paymentMethods'];
      paymentMethods[key] = Number(number.replace(',', '.'));
      return { ...prev, paymentMethods };
    });
  }, []);

  const handleSendCampaign = useCallback(() => {
    setState(prev => ({ ...prev, sendCampaign: !prev.sendCampaign }));
  }, []);

  const handleBankslipPurchase = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, bankslipPurchase: event.target.value }));
  }, []);

  const handleDelete = useCallback(
    async (idPixel: any, type: string) => {
      try {
        setState(prev => ({ ...prev, isFetching: true }));
        await deleteTrackerMutation.mutateAsync({ id: idPixel, type });
        await refetchTracker();
        toast.success('Pixel removido com sucesso!');
        setState(prev => ({ ...prev, isFetching: false, step: 1, domain: '' }));
      } catch (error) {
        toast.error(error);
      }
    },
    [deleteTrackerMutation, refetchTracker]
  );

  const handleSubmit = useCallback(async () => {
    const tracker: any = {
      type: state.platformType,
      pixelId: state.pixel,
      domain: state.domain,
      enabledPages: state.pages,
      forcePaymentValues: state.paymentMethods,
      tokenApi: state.tokenApi,
      contentId: product.id,
      hasCampaign: state.sendCampaign,
      hasPurchaseValue: true,
      conversionLabel: state.conversionLabel,
      bankslipPurchase: state.bankslipPurchase,
      validated: state.validated
    };

    if (state.purchaseValue === 'custom_values') {
      tracker.hasCustomValues = true;
    }

    if (state.purchaseValue === 'send_purchase_value') {
      tracker.hasCustomValues = false;
    }

    if (state.purchaseValue === 'never') {
      tracker.hasPurchaseValue = false;
    }

    try {
      setState(prev => ({ ...prev, isFetching: true }));
      await createTrackerMutation.mutateAsync({ tracker, id: state.id });
      setState(prev => ({ ...prev, domain: '', validated: false }));
      await refetchTracker();
      toast.success('Pixel cadastrado com sucesso!');
      setState(prev => ({ ...prev, isFetching: false, step: 1 }));
    } catch (error) {
      toast.error(error || 'Erro ao salvar o pixel');
      setState(prev => ({ ...prev, isFetching: false }));
    }
  }, [state, product.id, createTrackerMutation, refetchTracker]);

  return (
    <MyModal
      hasActive={open}
      closeModal={handleClose}
      hasButtons
      loading={state.isFetching || isLoadingTracker}
      hideButtonOk={state.step !== 2}
      title='Pixel de Rastreamento'
      subTitle='Selecione a plataforma que deseja cadastrar o pixel:'
      textButtonOk='Salvar'
      textButtonCancel='Fechar'
      style={{ transition: 'all 0.3s' }}
      handleClickOk={handleSubmit}
    >
      {state.step === 1 && (
        <Fragment>
          {state.isFetching ? (
            <div className='flex items-center justify-center p-8'>
              <Spin />
            </div>
          ) : (
            <Row gutter={[16, 16]}>
              {Platforms.map((item, index) => (
                <Col xs={24} sm={12} key={`platform-${index}`}>
                  <button
                    key={`platform-${index}`}
                    className={
                      'border-grey-300 hover:border-grey-500 hover:bg-grey-50 relative box-border flex w-full cursor-pointer items-center justify-center rounded-sm border bg-white px-4 py-8 hover:transition-[border-color,background-color,0.5s]'
                    }
                    onClick={() =>
                      handlePlatform(item.id, item.type, item.hasCampaign, item.hasPrice, item.hasBankslipPurchase, 2)
                    }
                  >
                    {state.registered.includes(item.type) && (
                      <div className='bg-success absolute top-1 right-1 rounded-full px-1 text-white'>
                        <CheckOutlined size={16} />
                      </div>
                    )}
                    <img src={item.image} alt={item.label} className='max-h-[35px] max-w-full' />
                  </button>
                </Col>
              ))}
            </Row>
          )}
        </Fragment>
      )}
      {state.step === 2 && (
        <div className='animate-fadeIn' style={{ marginRight: 16, animationDuration: '1s' }}>
          <TagmanagerAlert type={state.platformType} pixels={state.tracker?.pixel || []} />

          <Basic
            platformType={state.platformType}
            conversionLabel={state.conversionLabel}
            loading={state.isFetching}
            pixel={state.pixel}
            hasDomain={state.hasDomain}
            domain={state.domain}
            tokenApi={state.tokenApi}
            validated={state.validated}
            pixelId={state.id}
            handleTokenApi={handleTokenApi}
            back={handleBack}
            handleConversionLabel={handleConversionLabel}
            handleReload={handleVerifyDomain}
            handlePixel={handlePixel}
            handleDelete={handleDelete}
            handleDomain={handleDomain}
          />

          <button
            className='text-primary! cursor-pointer! border-none! bg-transparent! p-0! text-base! font-medium!'
            onClick={handleAdvanced}
          >
            {'Configurações adicionais  '}
            {state.advanced ? (
              <UpOutlined style={{ fontSize: 'inherit' }} />
            ) : (
              <DownOutlined style={{ fontSize: 'inherit' }} />
            )}
          </button>
          <div className='mt-6' />
          <Divider />
          <Advanced
            handlePageChange={handlePageChange}
            handleSendCampaign={handleSendCampaign}
            handleRadioChange={handleRadioChange}
            handleCustomValue={handleCustomValue}
            handleBankslipPurchase={handleBankslipPurchase}
            checkCustomValue={checkCustomValue}
            state={state}
          />
        </div>
      )}
    </MyModal>
  );
};

export default TrackerForm2;
